import React, { useState } from "react";
import PropTypes from "prop-types";
import { usePost } from "redux/fetch-hooks";

import { CardLevelOne } from "common/DS/ElevatedCard";
import { Subtitle2 } from "common/DS/Text";
import TextField from "common/DS/TextField";
import QuestionButtonGroup from "common/Question/QuestionButtonGroup";
import { heapTrack } from "lib/analytics";

export const CodeRequestCard = ({ setVisible, setContactInfo, showDisplayCode }, { t: _ }) => {
    const [hasError, setHasError] = useState(false);
    const [input, setInput] = useState("");
    const [helperText, setHelperText] = useState("");
    const accountFormatErrorText = _("Please enter a valid email or phone number");
    const [postAuthCode, { isLoading }] = usePost("/check-in/auth/code", {
        onSuccess: () => {
            setHelperText("");
            setVisible(false);
            showDisplayCode(true);
        },
        onError: (error) => {
            setHelperText(error.json.error);
            setHasError(true);
        },
    });

    function validate() {
        if (!input.length) {
            setHelperText(accountFormatErrorText);
            setHasError(true);
            return false;
        }

        return true;
    }
    function onNext() {
        setHasError(false);
        const canSubmit = validate();
        if (canSubmit) {
            setContactInfo(input);
            postAuthCode({
                data: {
                    csrf_token: window.csrfToken,
                    handle: input,
                    code_type: input.includes("@") ? "email" : "mobile",
                },
            });
        }
    }

    return (
        <HubCard
            labelText={_("Email or Phone Number")}
            placeholder={_("Email or Phone Number")}
            hasError={hasError}
            setHasError={setHasError}
            input={input}
            setInput={setInput}
            onNext={onNext}
            helperText={helperText}
            nextDisabled={isLoading}
        />
    );
};
CodeRequestCard.contextTypes = {
    t: PropTypes.func,
};
CodeRequestCard.propTypes = {
    setVisible: PropTypes.func.isRequired,
    setContactInfo: PropTypes.func.isRequired,
    showDisplayCode: PropTypes.func.isRequired,
};

export const EnterCodeCard = (
    { contactInfo, setVisible, setLoginVisible, setPageData },
    { t: _ },
) => {
    const [hasError, setHasError] = useState(false);
    const [input, setInput] = useState("");
    const [helperText, setHelperText] = useState("");
    const enterCodeSubtitle = _(`We sent a code to {contactInfo}`, { contactInfo });
    const buttonRenamer = { skip: _("Resend Code"), next: _("Log In") };
    const [postAuthCodeVerify, { isLoading }] = usePost("/check-in/auth/code/verify", {
        onSuccess: (data) => {
            heapTrack("Hub Login - redirecting to survey");
            setPageData({
                companyName: data.companyName,
                firstName: data.firstName,
                surveyList: data.surveyList,
            });
            setLoginVisible(false);
            setVisible(false);
        },
        onError: (error) => {
            setHelperText(error.json.error);
            setHasError(true);
        },
    });

    function onNext() {
        postAuthCodeVerify({
            data: {
                csrf_token: window.csrfToken,
                handle: contactInfo,
                code: input,
                code_type: contactInfo.includes("@") ? "email" : "mobile",
            },
        });
    }

    function onResend() {
        setInput("");
        setHasError(false);
        setHelperText("");
    }

    function onBack() {
        setHasError(false);
        setInput("");
        setHelperText("");
        setVisible(false);
        setLoginVisible(true);
    }

    return (
        <HubCard
            title={_("Enter Code")}
            subtitle={enterCodeSubtitle}
            labelText={_("Enter 6 digit code")}
            placeholder={_("Enter 6 digit code")}
            helperText={helperText}
            hasError={hasError}
            setHasError={setHasError}
            input={input}
            setInput={setInput}
            onNext={onNext}
            onResend={onResend}
            onBack={onBack}
            buttonRenamer={buttonRenamer}
            nextDisabled={isLoading}
        />
    );
};
EnterCodeCard.propTypes = {
    contactInfo: PropTypes.string.isRequired,
    setVisible: PropTypes.func.isRequired,
    setLoginVisible: PropTypes.func.isRequired,
    setPageData: PropTypes.func.isRequired,
};
EnterCodeCard.contextTypes = {
    t: PropTypes.func,
};

const HubCard = (
    {
        title,
        subtitle,
        labelText,
        placeholder,
        hasError,
        setHasError,
        input,
        setInput,
        onNext,
        onBack,
        onResend,
        helperText,
        buttonRenamer,
        nextDisabled,
    },
    { t: _ },
) => {
    const [hasInput, setHasInput] = useState(false);
    const buttonClassName = "hub__login-buttons";

    function onTextChange(e) {
        const textEntered = e.target.value.trim();
        setHasInput(textEntered.length > 0);
        setInput(textEntered);
        setHasError(false);
    }

    const pageTitle = title || _("Welcome to {company} Surveys!", { company: "WorkStep" });
    const pageSubtitle = subtitle || _("To begin your survey, please log in.");
    const pageLabelText = labelText || _("Email or Phone Number");
    const pagePlaceholder = placeholder || _("Email or Phone Number");

    return (
        <CardLevelOne className="hub__login">
            <div className="hub__login-title">{pageTitle}</div>
            <Subtitle2 className="hub__login-subtitle">{_(pageSubtitle)}</Subtitle2>
            <TextField
                className="hub__login-text-box"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={input}
                placeholder={pagePlaceholder}
                onChange={(e) => onTextChange(e)}
                label={input ? pageLabelText : ""}
                helperText={_(helperText)}
                error={hasError}
                useValidators={false}
            />
            <QuestionButtonGroup
                handleSubmit={onNext}
                handleBack={onBack}
                handleSkip={onResend}
                nextDisabled={!hasInput || nextDisabled}
                additionalClassNames={buttonClassName}
                buttonRenamer={buttonRenamer}
            />
        </CardLevelOne>
    );
};

HubCard.contextTypes = {
    t: PropTypes.func,
};
HubCard.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    labelText: PropTypes.string,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    input: PropTypes.string,
    setInput: PropTypes.func,
    hasError: PropTypes.bool,
    setHasError: PropTypes.func,
    onNext: PropTypes.func.isRequired,
    onBack: PropTypes.func,
    onResend: PropTypes.func,
    buttonRenamer: PropTypes.object,
    nextDisabled: PropTypes.bool,
};
HubCard.defaultProps = {
    title: null,
    subtitle: null,
    labelText: null,
    placeholder: null,
    helperText: "",
    hasError: false,
    setHasError: null,
    input: "",
    setInput: null,
    onBack: null,
    onResend: null,
    buttonRenamer: {},
    nextDisabled: false,
};
